import { BuilderContent } from "@builder.io/sdk";
import { useTranslation } from "next-i18next";
import React, { useEffect } from "react";

import { getFeatureFlag } from "@/shared/helpers/launch-darkly-server-helper";

import { parallel } from "@/utils/promises";
import serverSideTranslations from "@/utils/serverSideTranslations";

import { init } from "@/builder";
import { BuilderModel } from "@/builder/types";

import { HelpPage } from "@/components/help/landing/landing";
import { PublicArticle } from "@/components/help/types/article";
import { getMerchantRootToken } from "@/components/help/utils";

import { ChunkedNav, GenericNavItem } from "@/interfaces/navigation-interface";

import { Locale } from "@/types/locale";
import { GetServerSideProps } from "@/types/page";

import DefaultLayout from "../../layouts/default";
import { getBuilderMessages, getBuilderNavLinks } from "../api/builder/builder";
import { fetchHelpArticle } from "../api/help/get";
import { getFooterNavigation, getHeaderNavigation } from "../api/store-directory/navigation";

interface LocaleProps {
    locale: Locale;
}

type NavProps = {
    headerNavigationItems: ChunkedNav;
    footerLocalNavItems: ChunkedNav;
    footerRegionalNavItems: Array<Array<GenericNavItem>>;
};

type OwnProps = {
    builderMessages: Array<BuilderContent>;
    builderNavLinks: Array<BuilderContent>;
    helpData: PublicArticle | undefined;
};

type Props = OwnProps & NavProps & LocaleProps;

init(BuilderModel.MESSAGE);

export const getServerSideProps: GetServerSideProps<Props> = async ({ locale, resolvedUrl }) => {
    if (locale === "fr-CA") {
        return { redirect: { destination: `/${locale}`, permanent: false } };
    }

    const isEnabled = await getFeatureFlag("help-merchant-enabled", false, locale);

    if (!isEnabled) {
        return { redirect: { destination: `/${locale}`, permanent: false } };
    }

    const parallelProps = await parallel({
        headerNavigationItems: () => getHeaderNavigation(locale),
        footerNavItems: () => getFooterNavigation(locale),
        builderMessages: () => getBuilderMessages(locale, { url: resolvedUrl }),
        builderNavLinks: () => getBuilderNavLinks(locale),
        _nextI18Next: () => serverSideTranslations(locale),
        articleData: () => fetchHelpArticle(getMerchantRootToken(locale), locale, "merchant"),
    });

    return {
        props: {
            locale,
            footerLocalNavItems: parallelProps.footerNavItems[0],
            footerRegionalNavItems: parallelProps.footerNavItems[1],
            helpData: parallelProps.articleData.article,
            ...parallelProps,
        },
    };
};

const Help = ({
    locale,
    footerLocalNavItems,
    footerRegionalNavItems,
    helpData,
}: Props): React.ReactNode => {
    const { t } = useTranslation();

    useEffect(() => {
        document.body.classList.add("helpPage");
        return () => document.body.classList.remove("helpPage");
    }, []);

    return (
        <DefaultLayout
            currentLocale={locale}
            headerNavigationItems={[]}
            footerLocalNavItems={footerLocalNavItems}
            footerRegionalNavItems={footerRegionalNavItems}
            builderNavLinks={[]}
            builderMessages={[]}
            title={helpData?.metaTitle || helpData?.title || t("title")}
            description={helpData?.metaDescription || t("metaDescription")}
        >
            {helpData && <HelpPage helpData={helpData} type="merchant" />}
        </DefaultLayout>
    );
};

export default Help;
